





































































import { Component, Prop, Watch, Vue } from "vue-property-decorator";
import Dictionary from "@/interfaces/Dictionary";
import NumberFormatter from "@/services/NumberFormatter";

export const HEATMAP_SORT_ASC = "asc";
export const HEATMAP_SORT_DESC = "desc";

const HEATMAP_ROW_LIMIT = 8;
const HEATMAP_MIN_ELEMENTS = 3;
const HEATMAP_MAX_ELEMENTS = 5;

export interface HeatmapTableColumn {
  name: string;
  label: string;
  notSortable?: boolean;
  heading?: boolean;
  format?: Function;
  heatmapSort?: string;
  tooltip?: string;
  heatmapDisabled?: boolean | Function;
  locked?: boolean;
  disabled?: boolean;
  small?: boolean;
}

export interface HeatmapTableRow {
  // eslint-disable-next-line
  [key: string]: any;
  fixedAt?: "top" | "bottom";
}

export interface HeatmapTableHeatmap {
  [key: string]: number[];
}

@Component({ components: {} })
export default class HeatmapTable extends Vue {
  @Prop() readonly columns!: HeatmapTableColumn[];
  @Prop() readonly rows!: HeatmapTableRow[];
  @Prop({ default: null })
  readonly sortDefaultColumn!: HeatmapTableColumn | null;
  @Prop({ type: Boolean, default: true }) readonly responsive?: boolean;
  @Prop({ default: true }) readonly sortable?: boolean;
  @Prop({ default: false }) readonly heatmap?: boolean;
  @Prop({ default: false }) readonly loading!: boolean;

  @Watch("rows")
  onRowsChange(value: HeatmapTableRow[]) {
    this.values = [...value];
    this.sortValuesKeepFixed();

    this.$nextTick(() => {
      this.checkPosition();
    });
  }

  @Watch("sortDefaultColumn")
  onSortDefaultChange(value: HeatmapTableColumn) {
    this.orderBy.name = value.name;
    this.orderBy.sort = HEATMAP_SORT_DESC;
    this.sortValuesKeepFixed();
  }

  @Watch("columns")
  onColumnsChange() {
    this.$nextTick(() => {
      this.checkPosition();
    });
  }

  values: HeatmapTableRow[] = [];
  orderBy: {
    name: string;
    sort: string;
  } = {
    name: "",
    sort: HEATMAP_SORT_DESC,
  };

  position = {
    overflow: false,
    scrolledToRight: false,
    scrolledToLeft: true,
  };

  get heatmapColumns(): HeatmapTableColumn[] {
    if (this.heatmap == false) {
      return [];
    }

    return this.columns.filter((column) => {
      return column.heading != true;
    });
  }

  get heatmapValues(): HeatmapTableHeatmap {
    if (this.heatmap == false) {
      return {};
    }

    const values: HeatmapTableHeatmap = {};

    this.heatmapColumns.forEach((column) => {
      values[column.name] = this.values.map((row) => {
        if (
          row[column.name]?.type == "quoted" ||
          row[column.name]?.type == "trend"
        ) {
          return row[column.name].value as number;
        } else {
          return row[column.name] as number;
        }
      });

      values[column.name].sort((a: number, b: number) => {
        const heatmapSort = column.heatmapSort || HEATMAP_SORT_DESC;

        if (a < b) {
          return heatmapSort == HEATMAP_SORT_ASC ? -1 : 1;
        } else if (a > b) {
          return heatmapSort == HEATMAP_SORT_DESC ? -1 : 1;
        }

        return 0;
      });
    });

    return values;
  }

  getElementWidth(): number {
    return this.$refs.inner
      ? (this.$refs.inner as HTMLDivElement).clientWidth
      : 0;
  }

  getTableWidth(): number {
    return this.$refs.table
      ? (this.$refs.table as HTMLTableElement).clientWidth
      : 0;
  }

  created() {
    this.values = [...this.rows];
  }

  destroyed() {
    window.removeEventListener("resize", this.checkPosition);
  }

  mounted() {
    window.addEventListener("resize", this.checkPosition);
    this.checkPosition();
  }

  checkPosition() {
    this.position.overflow =
      (this.responsive || true) &&
      this.getElementWidth() < this.getTableWidth();
    this.position.scrolledToRight =
      (this.$refs.inner as HTMLDivElement).scrollLeft ==
      this.getTableWidth() - this.getElementWidth();
    this.position.scrolledToLeft =
      (this.$refs.inner as HTMLDivElement).scrollLeft == 0;
  }

  sortColumn(column: HeatmapTableColumn) {
    if (this.sortable == false || column.notSortable == true) {
      return;
    }

    let sort = HEATMAP_SORT_DESC;

    if (this.orderBy.name == column.name) {
      sort =
        this.orderBy.sort == HEATMAP_SORT_ASC
          ? HEATMAP_SORT_DESC
          : HEATMAP_SORT_ASC;
    }

    this.orderBy.name = column.name;
    this.orderBy.sort = sort;

    this.sortValuesKeepFixed();
  }

  sortValuesKeepFixed() {
    if (this.orderBy.name == undefined || this.orderBy.name == "") {
      return;
    }

    const { topRows, middleRows, bottomRows } = {
      topRows: this.values.filter((row) => row.fixedAt === "top"),
      middleRows: this.values.filter((row) => !row.fixedAt),
      bottomRows: this.values.filter((row) => row.fixedAt === "bottom"),
    };

    this.values = [
      ...this.sortRows(topRows),
      ...this.sortRows(middleRows),
      ...this.sortRows(bottomRows),
    ];
  }

  private sortRows(rows: HeatmapTableRow[]) {
    return rows.sort((a: HeatmapTableRow, b: HeatmapTableRow) => {
      const aValue = a[this.orderBy.name] || "";
      const bValue = b[this.orderBy.name] || "";

      if (this.compareRows(aValue, bValue)) {
        return this.orderBy.sort == HEATMAP_SORT_ASC ? -1 : 1;
      } else if (this.compareRows(bValue, aValue)) {
        return this.orderBy.sort == HEATMAP_SORT_ASC ? 1 : -1;
      }

      return 0;
    });
  }

  compareRows(a: any, b: any) {
    if (
      (a.type == "quoted" || a.type == "trend") &&
      (b.type == "quoted" || b.type == "trend")
    ) {
      return a.value < b.value;
    } else if (this.orderBy.name === "averagePosition") {
      const aMatch = a.match(/(.+)\/(.+)/);
      const bMatch = b.match(/(.+)\/(.+)/);
      const aAvg = aMatch[2] / aMatch[1];
      const bAvg = bMatch[2] / bMatch[1];

      if (isNaN(aAvg) || isNaN(bAvg)) {
        return -1;
      } else {
        return aAvg < bAvg;
      }
    } else {
      if (a == "N/A") {
        return -1;
      } else {
        return a < b;
      }
    }
  }

  scrollToLeft() {
    (this.$refs.inner as HTMLDivElement).scrollLeft = 0;
    this.checkPosition();
  }

  scrollToRight() {
    (this.$refs.inner as HTMLDivElement).scrollLeft =
      this.getTableWidth() - this.getElementWidth();
    this.checkPosition();
  }

  protected isSortable(column: HeatmapTableColumn) {
    if (this.sortable == false) {
      return false;
    }

    return (column.notSortable || column.disabled) != true;
  }

  protected getSlotName(column: HeatmapTableColumn) {
    return `column_${column.name}`;
  }

  protected getFullRowSlotName(column: HeatmapTableColumn) {
    return `full_row_column_${column.name}`;
  }

  protected hasSlot(column: HeatmapTableColumn) {
    const slotName = this.getSlotName(column);
    return this.$scopedSlots[slotName] != null;
  }

  protected hasFullRowSlot(column: HeatmapTableColumn) {
    const slotName = this.getFullRowSlotName(column);
    return this.$scopedSlots[slotName] != null;
  }

  protected getColumnClass(column: HeatmapTableColumn) {
    const sortable = this.isSortable(column);
    const columnClass: Dictionary = {
      "heatmap-table__column": true,
      "heatmap-table__column--sortable": sortable,
      "heatmap-table__column--sorted":
        sortable && column.name == this.orderBy.name,
      "heatmap-table__column--sorted--asc":
        column.name == this.orderBy.name &&
        this.orderBy.sort == HEATMAP_SORT_ASC,
      "heatmap-table__column--sorted--desc":
        column.name == this.orderBy.name &&
        this.orderBy.sort == HEATMAP_SORT_DESC,
      "heatmap-table__column--head": column.heading,
      "heatmap-table__column--locked": column.locked,
      "heatmap-table__column--small": column.small,
    };
    return columnClass;
  }

  protected getCellValue(
    column: HeatmapTableColumn,
    row: HeatmapTableRow
  ): string | number | undefined {
    if (
      row[column.name] &&
      row[column.name].type &&
      (row[column.name].type == "quoted" || row[column.name].type == "trend")
    ) {
      return row[column.name].value;
    }
    return row[column.name];
  }

  protected getCellDisplayValue(
    column: HeatmapTableColumn,
    row: HeatmapTableRow
  ): string | number | object | undefined {
    const value = this.getCellValue(column, row);

    if (value == undefined) {
      return undefined;
    }
    if (row[column.name].type == "quoted") {
      return row[column.name].quote;
    }

    if (row[column.name].type == "trend") {
      const res = `${NumberFormatter.format(row[column.name].value)}`;
      if (row[column.name].trend == 0) {
        return res;
      }
      if (row[column.name].trend > 0) {
        return `${res} <span class="quote__positive">+${Math.floor(
          row[column.name].trend
        ).toFixed(0)}%</span>`;
      }
      if (row[column.name].trend < 0) {
        return `${res} <span class="quote__negative">${Math.floor(
          row[column.name].trend
        ).toFixed(0)}%</span>`;
      }
    }

    if (typeof column.format == "function") {
      return column.format(value);
    }

    if (typeof value == "number") {
      return NumberFormatter.format(value);
    }

    return value;
  }

  protected getCellClass(column: HeatmapTableColumn, row: HeatmapTableRow) {
    const columnClass = this.getColumnClass(column);
    const heatmapDisabled =
      typeof column.heatmapDisabled == "function"
        ? column.heatmapDisabled(row)
        : column.heatmapDisabled == true;

    if (this.heatmap == false || column.heading || heatmapDisabled) {
      return columnClass;
    }

    const index = this.getHeatmapIndex(column, row);

    const cellClass: Dictionary = {};

    if (
      index != undefined &&
      ((this.values.length <= HEATMAP_ROW_LIMIT &&
        index < HEATMAP_MIN_ELEMENTS) ||
        (this.values.length > HEATMAP_ROW_LIMIT &&
          index < HEATMAP_MAX_ELEMENTS))
    ) {
      cellClass[`heatmap-${index}`] = true;
    }

    return {
      ...columnClass,
      ...cellClass,
    };
  }

  protected getHeatmapIndex(
    column: HeatmapTableColumn,
    row: HeatmapTableRow
  ): number | undefined {
    if (
      this.heatmapValues[column.name] == undefined ||
      row[column.name] == undefined
    ) {
      return undefined;
    }

    const value =
      row[column.name].type == "quoted" || row[column.name].type == "trend"
        ? row[column.name].value
        : (row[column.name] as number) || 0;

    if (
      value == 0 ||
      (this.heatmapValues[column.name].length &&
        this.heatmapValues[column.name][0] == 0) ||
      isNaN(parseFloat(value as string))
    ) {
      return undefined;
    }

    return this.heatmapValues[column.name].indexOf(value);
  }

  protected getColumnTooltipOptions(column: HeatmapTableColumn) {
    if (column.tooltip == undefined) {
      return {};
    }

    return {
      content: column.tooltip,
      placement: "bottom",
      offset: 0,
    };
  }
}
